import { FC, useEffect, useRef, useState } from 'react'
import { Box } from '@/components/atoms/Grid'
import { Body } from '@/components/atoms/typography'
import ExpandableSectionBox from '@/components/molecules/expandableSectionBox'
import Link from 'components/atoms/link'
import styled from 'styled-components'
import { toggleExpandedIndex } from '@/core/utils'
import { makeItems, ItemType, Item } from './contentCategoryMenuData'
import { SlugPath } from 'types/slugPath'
import { validFor } from '@/theme/validFor'
import useTranslation from 'next-translate/useTranslation'
import { Category } from '@/network/graphql.g'
import { ID } from 'graphql-ws'
import {
  Desktop,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import MenuBadge from '@/components/atoms/menuBadge'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { NextRouter, useRouter } from 'next/router'
import { CategoryIconsList } from '@/components/atoms/svg/categoryIcons/categoryIconsList'

export enum ContentCategoryMenuLayoutType {
  INSIDE_MENU,
  ON_PAGE
}

type ContentCategoryMenuProps = {
  subcategories: Category[]
  slugPath: SlugPath
  layoutType?: ContentCategoryMenuLayoutType
} & Rest

const findSelected = (data: Item[]) => {
  if (data) {
    for (let index = 0; index < data.length; index++) {
      const item = data[index]
      if (item.isSelected) {
        return item
      } else {
        const subItem = findSelected(item.subItems)
        if (subItem) {
          return subItem
        }
      }
    }
  }
  return null
}

const ContentCategoryMenu: FC<ContentCategoryMenuProps> = ({
  subcategories,
  slugPath,
  layoutType = ContentCategoryMenuLayoutType.ON_PAGE,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const [expandedIds, setExpandedIds] = useState<ID[]>([])
  const onceExtendMenuRef = useRef<boolean>(null)
  const checkIfIsOpened = (subcategoryId: ID): boolean =>
    expandedIds.includes(subcategoryId)
  useEffect(() => {
    onceExtendMenuRef.current = true
    if (slugPath?.subcategoryId) {
      setExpandedIds([slugPath?.subcategoryId?.id])
    }
  }, [slugPath])
  const router = useRouter()

  const filterCategories = (items: Category[]) =>
    items?.filter(({ visibleInNavigation }) => visibleInNavigation)

  const filteredSubcategories = filterCategories(subcategories)

  return (
    <Box {...rest} data-testid="contentCategoryMenuDesktop">
      {filteredSubcategories &&
        filteredSubcategories.map(
          (
            { id, title, children, mobileBadge, desktopBadge, isHighlight },
            offset
          ) => {
            const filteredChildren = filterCategories(children)
            const showItems =
              !onceExtendMenuRef.current ||
              (checkIfIsOpened(id) && filteredChildren.length > 0)
            const items = showItems
              ? makeItems(
                  [
                    {
                      id,
                      title: t('Category.showAll'),
                      status: true,
                      url: `/category/${id}`,
                      visibleInNavigation: true,
                      isMainCategory: true
                    },
                    ...filteredChildren
                  ],
                  ItemType.subcategoryItem,
                  slugPath,
                  [id]
                )
              : []
            const isFilter =
              !!findSelected(items) || slugPath.subcategoryId?.id === id
            return (
              <Box key={id} data-testid="contentCategoryItemsDesktop">
                <ExpandableSectionBox
                  showTopSeparator={
                    layoutType != ContentCategoryMenuLayoutType.INSIDE_MENU ||
                    offset > 0
                  }
                  onClick={() =>
                    setExpandedIds(toggleExpandedIndex(id, expandedIds))
                  }
                  showBottomSeparator={
                    checkIfIsOpened(id) ||
                    offset + 1 === filteredSubcategories.length
                  }
                  title={title}
                  isOpened={checkIfIsOpened(id)}
                  mobileBadge={mobileBadge}
                  desktopBadge={desktopBadge}
                  highlight={isHighlight}
                />
                {showItems && (
                  <Box my={20}>
                    {makeMenuItems(items, false, isFilter, router)}
                  </Box>
                )}
              </Box>
            )
          }
        )}
    </Box>
  )
}

const makeMenuItems = (
  items: Item[],
  intend: boolean,
  isFilter: boolean,
  router: NextRouter
) => {
  const query =
    isFilter && router
      ? convertQueryParamsAndRemoveEmptyGetParameters({
          query: router.query,
          allowOnly: [
            FilterQueryItem.STOCK,
            FilterQueryItem.MAIN_WAREHOUSE,
            FilterQueryItem.NEWS,
            FilterQueryItem.SIZE,
            FilterQueryItem.COLOR,
            FilterQueryItem.MATERIAL,
            FilterQueryItem.MANUFACTURER,
            FilterQueryItem.PRICE_MIN,
            FilterQueryItem.PRICE_MAX
          ]
        })
      : null

  return (
    <Box ml={intend ? 10 : 0} key={items[0]?.id}>
      {items.map(
        ({
          isSelected,
          id,
          title,
          subItems,
          url,
          mobileBadge,
          desktopBadge,
          highlight
        }) => {
          return url ? (
            <Box key={id}>
              <StyledLink
                href={`${url}${query ? `?${query}` : ''}`}
                data-testid="categoryLink"
              >
                <StyledIcon>
                  {CategoryIconsList?.[id] && CategoryIconsList?.[id]}
                </StyledIcon>
                <StyledBody isSelected={isSelected} highlight={highlight}>
                  {title}
                </StyledBody>
                <MediaContextProvider>
                  <Desktop>
                    {desktopBadge && (
                      <MenuBadge
                        imageUrl={desktopBadge}
                        maxHeight={20}
                        ml={10}
                      />
                    )}
                  </Desktop>
                  <TabletOrMobile>
                    {(mobileBadge || desktopBadge) && (
                      <MenuBadge
                        imageUrl={mobileBadge || desktopBadge}
                        maxHeight={20}
                        ml={10}
                      />
                    )}
                  </TabletOrMobile>
                </MediaContextProvider>
              </StyledLink>
              {subItems && makeMenuItems(subItems, true, isFilter, router)}
            </Box>
          ) : (
            <Box>{title}</Box>
          )
        }
      )}
    </Box>
  )
}

export default ContentCategoryMenu

const StyledBody = styled<{ isSelected: boolean }>(Body)`
  opacity: ${(props) => (props.isSelected ? 1 : 0.8)};
  font-weight: ${(props) =>
    props.isSelected || props.highlight ? 'bold' : 'normal'};
  margin: 3px 0;
  line-height: 1.86;
  &:hover {
    opacity: 1;
    cursor: pointer;
  }
  ${validFor.mobile`
    font-size: 14px;
  `}
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
`
const StyledIcon = styled(Box)`
  padding-right: 8px;
`
